<template>
  <div class="yunpian-shot-link-container">
    <div class="header-section">
      <div class="button-group">
        <el-button type="primary" @click="downloadTemplate" class="btn-header">
          📥 下载模板
        </el-button>
        <el-upload action="#" :auto-upload="false" :on-change="handleFileUpload" accept=".xlsx, .xls" :show-file-list="false">
          <el-button slot="trigger" type="success" class="btn-header">📤 导入Excel</el-button>
        </el-upload>
        <el-button type="warning" @click="showSingleGenerateDialog = true" class="btn-header">
          单个生成
        </el-button>
      </div>
      <div style="text-align: center; color: #666; font-size: 12px; margin-top: 8px;">
        提示：Excel表头为 "链接名称" 和 "长链接"，请按照模板格式导入
      </div>
    </div>

    <!-- 单个生成对话框 -->
    <el-dialog v-model="showSingleGenerateDialog" title="单个生成短链接" width="500px" :close-on-click-modal="false">
      <el-form :model="singleLinkForm" label-width="100px">
        <el-form-item label="链接名称">
          <el-input v-model="singleLinkForm.name" placeholder="请输入链接名称" :disabled="singleGenerating" />
        </el-form-item>
        <el-form-item label="长链接">
          <el-input v-model="singleLinkForm.longUrl" placeholder="请输入以http://或https://开头的链接" type="textarea" :rows="3" :disabled="singleGenerating" />
        </el-form-item>
      </el-form>
      <div v-if="singleLinkForm.result" class="single-result">
        <div class="result-title">生成结果：</div>
        <div class="result-content">
          <span>{{ singleLinkForm.result }}</span>
          <el-button type="text" size="small" @click="copySingleLink">
            复制
          </el-button>
        </div>
      </div>
      <template #footer>
        <el-button @click="resetSingleForm" :disabled="singleGenerating">取消</el-button>
        <el-button type="primary" @click="generateSingleLink" :loading="singleGenerating" :disabled="!singleLinkForm.name || !singleLinkForm.longUrl">
          {{ singleGenerating ? '生成中...' : '生成' }}
        </el-button>
      </template>
    </el-dialog>

    <!-- 多Sheet选择对话框 -->
    <el-dialog v-model="showSheetDialog" title="选择Sheet" width="500px" :close-on-click-modal="false">
      <p style="margin-bottom: 20px;">检测到Excel文件有多个Sheet，请选择要处理的Sheet（支持多选）：</p>
      <div class="sheet-list">
        <el-checkbox-group v-model="selectedSheets">
          <el-checkbox v-for="(sheet, index) in sheetNames" :key="index" :label="sheet" style="display: block; margin-bottom: 10px;">
            {{ sheet }}
          </el-checkbox>
        </el-checkbox-group>
      </div>
      <template #footer>
        <el-button @click="showSheetDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmSheetSelection" :disabled="selectedSheets.length === 0">确认</el-button>
      </template>
    </el-dialog>

    <!-- 预览表格（支持多Sheet切换） -->
    <div v-if="Object.keys(previewDataMap).length > 0" class="preview-section">
      <h3>预览与编辑</h3>
      
      <!-- Sheet 标签页 -->
      <div class="sheet-tabs">
        <button 
          v-for="sheetName in activeSheetNames" 
          :key="sheetName"
          :class="['sheet-tab', { active: currentPreviewSheet === sheetName }]"
          @click="currentPreviewSheet = sheetName">
          {{ sheetName }}
        </button>
      </div>

      <el-table :data="previewDataMap[currentPreviewSheet]" style="width: 100%; margin-bottom: 20px;" :max-height="400">
        <el-table-column align="center" prop="name" label="链接名称" min-width="200">
          <template #default="scope">
            <el-input v-model="scope.row.name" placeholder="请输入链接名称" :disabled="isGenerating" @change="validateRow(scope.$index)" />
          </template>
        </el-table-column>
        <el-table-column align="center" prop="longUrl" label="长链接" min-width="300">
          <template #default="scope">
            <el-input v-model="scope.row.longUrl" placeholder="请输入长链接" type="textarea" :rows="2" :disabled="isGenerating" @change="validateRow(scope.$index)" />
          </template>
        </el-table-column>
        <el-table-column align="center" prop="shortUrl" label="短链接" min-width="180">
          <template #default="scope">
            <span v-if="scope.row.shortUrl" class="short-link">{{ scope.row.shortUrl }}</span>
            <span v-else class="status-text">{{ scope.row.status }}</span>
          </template>
        </el-table-column>
        <el-table-column align="center" label="操作" min-width="130">
          <template #default="scope">
            <el-button type="danger" size="small" :disabled="isGenerating" @click="removeRow(scope.$index)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 验证错误提示 -->
      <div v-if="validationErrors.length > 0" class="error-section">
        <el-alert type="error" title="验证错误" :closable="false">
          <ul style="margin: 0; padding-left: 20px;">
            <li v-for="error in validationErrors" :key="error">{{ error }}</li>
          </ul>
        </el-alert>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button type="success" :loading="isGenerating" :disabled="validationErrors.length > 0 || getTotalRowCount() === 0" @click="batchGenerateShortLinks" class="btn-action">
          {{ isGenerating ? '生成中...' : '批量生成短链' }}
        </el-button>
        <el-button type="primary" :disabled="getTotalRowCount() === 0 || !hasGeneratedLinks" @click="downloadResult" class="btn-action">
          下载结果
        </el-button>
      </div>
    </div>

    <!-- 空状态提示 -->
    <div v-else class="empty-state">
      <div class="empty-icon">📄</div>
      <p>请先下载模板或导入Excel文件</p>
    </div>
  </div>
</template>

<script>
import api from '@/utils/http.js';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as XLSX from 'xlsx';

export default {
  name: 'YunpianShotLink',
  data() {
    return {
      previewDataMap: {}, // 改为 Map 存储多个 sheet 的数据
      isGenerating: false,
      showSheetDialog: false,
      sheetNames: [],
      selectedSheets: [], // 改为数组支持多选
      pendingWorkbook: null,
      validationErrors: [],
      hasGeneratedLinks: false,
      showSingleGenerateDialog: false,
      singleGenerating: false,
      currentPreviewSheet: '', // 当前预览的 sheet
      singleLinkForm: {
        name: '',
        longUrl: '',
        result: ''
      }
    };
  },
  computed: {
    activeSheetNames() {
      return Object.keys(this.previewDataMap);
    },
    previewData() {
      // 兼容旧代码
      return this.previewDataMap[this.currentPreviewSheet] || [];
    }
  },
  methods: {
    downloadTemplate() {
      const templateUrl = 'https://morefun-k8s-data.oss-cn-zhangjiakou.aliyuncs.com/jdyd/chanel/channel-tools/yp-service/template/yp_shot_link_template.xlsx';
      
      try {
        // 使用 fetch 下载文件
        fetch(templateUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error('下载失败');
            }
            return response.blob();
          })
          .then(blob => {
            // 创建临时 URL 并触发下载
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'yp_shot_link_template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            ElMessage.success('模板下载成功');
          })
          .catch(error => {
            console.error('模板下载失败:', error);
            ElMessage.error('模板下载失败，请检查网络连接');
          });
      } catch (error) {
        console.error('下载错误:', error);
        ElMessage.error('模板下载失败');
      }
    },

    handleFileUpload(event) {
      if (!event || !event.raw) {
        ElMessage.error('文件上传事件无效，请重试');
        return;
      }

      const file = event.raw;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // 检查是否有多个sheet
          if (workbook.SheetNames.length > 1) {
            this.sheetNames = workbook.SheetNames;
            this.selectedSheets = []; // 清空之前的选择
            this.pendingWorkbook = workbook;
            this.showSheetDialog = true;
          } else {
            this.processWorkbook(workbook, [0]);
          }
        } catch (error) {
          console.error('Excel读取错误:', error);
          ElMessage.error('Excel文件读取失败，请检查文件格式');
        }
      };

      reader.readAsArrayBuffer(file);
    },

    confirmSheetSelection() {
      if (this.selectedSheets.length === 0) {
        ElMessage.error('请至少选择一个Sheet');
        return;
      }

      const sheetIndices = this.selectedSheets.map(sheetName => 
        this.sheetNames.indexOf(sheetName)
      );
      this.processWorkbook(this.pendingWorkbook, sheetIndices);
      this.showSheetDialog = false;
      this.pendingWorkbook = null;
    },

    processWorkbook(workbook, sheetIndices) {
      try {
        // 支持单个索引（旧用法）或数组（新用法）
        const indices = Array.isArray(sheetIndices) ? sheetIndices : [sheetIndices];
        
        this.previewDataMap = {}; // 重置数据
        let totalRows = 0;

        indices.forEach(sheetIndex => {
          const sheetName = workbook.SheetNames[sheetIndex];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);

          if (json.length === 0) {
            return; // 跳过空 sheet
          }
          
          // 获取所有行的所有列名
          const allHeaders = new Set();
          json.forEach(row => {
            Object.keys(row).forEach(key => {
              allHeaders.add(key);
            });
          });
          const headers = Array.from(allHeaders);
          console.log(`[${sheetName}] 表头列名:`, headers);

          // 查找匹配的列
          let nameKey = null;
          let urlKey = null;
          
          for (const key of headers) {
            const normalizedKey = key.trim().toLowerCase();
            if (normalizedKey === '链接名称' || normalizedKey === 'name') {
              nameKey = key;
            } else if (normalizedKey === '长链接' || normalizedKey === 'longurl' || normalizedKey === 'long_url' || normalizedKey === 'url') {
              urlKey = key;
            }
          }

          console.log(`[${sheetName}] 识别的列: nameKey=${nameKey}, urlKey=${urlKey}`);
          
          // 如果没有找到，使用前两列
          if (!nameKey || !urlKey) {
            nameKey = headers[0];
            urlKey = headers[1];
          }

          // 解析数据
          const sheetData = json.map(row => {
            const name = String(row[nameKey] || '').trim();
            const longUrl = String(row[urlKey] || '').trim();

            return {
              name: name,
              longUrl: longUrl,
              shortUrl: '',
              status: '未生成'
            };
          });

          // 过滤掉空行
          const filteredData = sheetData.filter(row => row.name || row.longUrl);
          this.previewDataMap[sheetName] = filteredData;
          totalRows += filteredData.length;
        });

        // 设置当前预览的 sheet
        const firstSheet = Object.keys(this.previewDataMap)[0];
        if (firstSheet) {
          this.currentPreviewSheet = firstSheet;
        }

        this.hasGeneratedLinks = false;
        this.validateAll();

        if (totalRows === 0) {
          ElMessage.warning('文件中没有有效的数据');
        } else {
          ElMessage.success(`成功导入 ${totalRows} 行数据（${Object.keys(this.previewDataMap).length} 个Sheet）`);
        }
      } catch (error) {
        console.error('数据处理错误:', error);
        ElMessage.error('数据处理失败，请检查Excel格式');
      }
    },

    validateRow(index) {
      this.validateAll();
    },

    validateAll() {
      this.validationErrors = [];

      // 遍历所有 sheet 的数据
      Object.entries(this.previewDataMap).forEach(([sheetName, rows]) => {
        rows.forEach((row, index) => {
          if (!row.name || row.name.trim() === '') {
            this.validationErrors.push(`[${sheetName}] 第 ${index + 1} 行：链接名称不能为空`);
          }
          if (!row.longUrl || row.longUrl.trim() === '') {
            this.validationErrors.push(`[${sheetName}] 第 ${index + 1} 行：长链接不能为空`);
          } else if (!this.isValidUrl(row.longUrl)) {
            this.validationErrors.push(`[${sheetName}] 第 ${index + 1} 行：长链接必须以 http:// 或 https:// 开头`);
          }
        });
      });
    },

    isValidUrl(url) {
      const trimmedUrl = url.trim().toLowerCase();
      return trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');
    },

    removeRow(index) {
      this.previewDataMap[this.currentPreviewSheet].splice(index, 1);
      this.validateAll();
    },

    getTotalRowCount() {
      return Object.values(this.previewDataMap).reduce((total, rows) => total + rows.length, 0);
    },

    async batchGenerateShortLinks() {
      if (this.validationErrors.length > 0) {
        ElMessage.error('请先修正所有的验证错误');
        return;
      }

      const totalRows = this.getTotalRowCount();
      if (totalRows === 0) {
        ElMessage.error('没有数据可以生成');
        return;
      }

      // 确认操作
      try {
        await ElMessageBox.confirm(
          `确定要生成 ${totalRows} 个短链接吗？`,
          '确认操作',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
      } catch {
        return;
      }

      this.isGenerating = true;

      try {
        // 遍历所有 sheet，为每个 sheet 的数据生成短链接
        for (const [sheetName, rows] of Object.entries(this.previewDataMap)) {
          // 准备请求数据
          const requestData = rows.map(row => ({
            name: row.name.trim(),
            long_url: row.longUrl.trim()
          }));

          // 调用 API 生成短链接
          const response = await api.post(
            '/chanel-yp-api/yp-api-service/api/v1/shot/link/create/batch',
            requestData
          );

          if (response.code === 200 && response.data) {
            // 将生成的短链接回填到对应的 sheet 数据
            response.data.forEach((item, index) => {
              if (index < rows.length) {
                rows[index].shortUrl = item.short_url;
                rows[index].status = '已生成';
              }
            });
          } else {
            ElMessage.error(`[${sheetName}] 生成失败，请重试`);
          }
        }

        this.hasGeneratedLinks = true;
        ElMessage.success('短链接批量生成成功！');
      } catch (error) {
        console.error('生成短链接失败:', error);
        ElMessage.error('生成短链接失败，请检查网络连接');
      } finally {
        this.isGenerating = false;
      }
    },

    downloadResult() {
      if (this.getTotalRowCount() === 0) {
        ElMessage.error('没有数据可以下载');
        return;
      }

      try {
        // 为每个 sheet 准备导出数据
        const workbook = XLSX.utils.book_new();

        Object.entries(this.previewDataMap).forEach(([sheetName, rows]) => {
          // 准备导出数据
          const exportData = rows.map(row => ({
            '链接名称': row.name,
            '长链接': row.longUrl,
            '短链接': row.shortUrl || ''
          }));

          const worksheet = XLSX.utils.json_to_sheet(exportData);
          // 设置列宽
          worksheet['!cols'] = [{ wch: 20 }, { wch: 50 }, { wch: 30 }];

          // 将 sheet 添加到工作簿
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        const filename = `yp_shot_link_result_${new Date().getTime()}.xlsx`;
        XLSX.writeFile(workbook, filename);

        ElMessage.success('文件下载成功');
      } catch (error) {
        console.error('下载失败:', error);
        ElMessage.error('文件下载失败');
      }
    },

    async generateSingleLink() {
      // 验证输入
      if (!this.singleLinkForm.name || this.singleLinkForm.name.trim() === '') {
        ElMessage.error('请输入链接名称');
        return;
      }

      if (!this.singleLinkForm.longUrl || this.singleLinkForm.longUrl.trim() === '') {
        ElMessage.error('请输入长链接');
        return;
      }

      if (!this.isValidUrl(this.singleLinkForm.longUrl)) {
        ElMessage.error('长链接必须以 http:// 或 https:// 开头');
        return;
      }

      this.singleGenerating = true;
      this.singleLinkForm.result = '';

      try {
        const response = await api.post(
          '/chanel-yp-api/yp-api-service/api/v1/shot/link/create',
          {
            name: this.singleLinkForm.name.trim(),
            long_url: this.singleLinkForm.longUrl.trim()
          }
        );

        if (response.code === 200 && response.data) {
          this.singleLinkForm.result = response.data.short_url;
          ElMessage.success('短链接生成成功！');
        } else {
          ElMessage.error(response.message || '生成失败，请重试');
        }
      } catch (error) {
        console.error('生成短链接失败:', error);
        ElMessage.error('生成短链接失败，请检查网络连接');
      } finally {
        this.singleGenerating = false;
      }
    },

    copySingleLink() {
      if (this.singleLinkForm.result) {
        navigator.clipboard.writeText(this.singleLinkForm.result)
          .then(() => {
            ElMessage.success('复制成功');
          })
          .catch(err => {
            console.error('复制失败', err);
            ElMessage.error('复制失败，请重试');
          });
      }
    },

    resetSingleForm() {
      this.singleLinkForm = {
        name: '',
        longUrl: '',
        result: ''
      };
      this.showSingleGenerateDialog = false;
    }
  }
};
</script>

<style scoped>
.yunpian-shot-link-container {
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.header-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.button-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-header {
  height: 40px;
  padding: 0 20px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.btn-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.preview-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preview-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.short-link {
  color: #409eff;
  word-break: break-all;
  font-size: 12px;
}

.status-text {
  color: #909399;
  font-size: 12px;
}

.error-section {
  margin: 20px 0;
}

.error-section ul {
  color: #f56c6c;
  font-size: 13px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.btn-action {
  height: 40px;
  padding: 0 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.sheet-list {
  padding: 10px 0;
}

.sheet-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid #dcdfe6;
  padding-bottom: 12px;
  flex-wrap: wrap;
}

.sheet-tab {
  padding: 8px 16px;
  border: none;
  background-color: transparent;
  color: #606266;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  bottom: -1px;
}

.sheet-tab:hover {
  color: #409eff;
}

.sheet-tab.active {
  color: #409eff;
  border-bottom-color: #409eff;
  font-weight: 500;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: #fff;
  border-radius: 4px;
  color: #909399;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 14px;
  color: #606266;
}

.single-result {
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
}

.result-title {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
  font-weight: 500;
}

.result-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background-color: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  word-break: break-all;
  color: #409eff;
  font-size: 13px;
}

/* 表格输入框样式优化 */
:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  font-size: 13px;
  padding: 8px 12px;
}

:deep(.el-input) {
  width: 100%;
}

:deep(.el-table) {
  font-size: 13px;
}

:deep(.el-table .el-button) {
  padding: 5px 10px;
  font-size: 12px;
}

:deep(.el-dialog__header) {
  border-bottom: 1px solid #ebeef5;
}

:deep(.el-form-item) {
  margin-bottom: 18px;
}
</style>
